import React, { useEffect, useState } from "react";
import {
  ComponentConfig,
  DropZone,
  WithId,
  WithPuckProps,
} from "@measured/puck";

export interface MasonryGridProps {
  options: {
    desktop?: {
      itemCount: number;
      columnCount?: number;
      gap?: number;
      placeContent?: "center" | "end" | "start" | "normal";
    };
    tablet?: {
      itemCount: number;
      columnCount?: number;
      gap?: number;
      placeContent?: "center" | "end" | "start" | "normal";
    };
    mobile?: {
      itemCount: number;
      columnCount?: number;
      gap?: number;
      placeContent?: "center" | "end" | "start" | "normal";
    };
  };
}

const MasonryGrid = (props: WithId<WithPuckProps<MasonryGridProps>>) => {
  const { id, options } = props;
  const className = `masonry-grid-${id}`;

  const [viewport, setViewport] = useState<"mobile" | "tablet" | "desktop">(
    "desktop",
  );

  useEffect(() => {
    const width = props.editMode
      ? (document.getElementById("preview-frame")?.clientWidth ?? 1024)
      : window.innerWidth;

    if (width < 744) setViewport("mobile");
    else if (width < 1024) setViewport("tablet");
    else setViewport("desktop");
  }, []);

  const css = `
        @media only screen and (max-width: 743px) {
          .${className} {
            column-count: ${options.mobile.columnCount ?? 1};
            column-gap: ${options.mobile.gap ?? 24}px;
          }

          .${className} > * {
            break-inside: avoid;
            margin-bottom: ${options.mobile.gap ?? 24}px;
          }
        }

        @media only screen and (min-width: 744px) and (max-width: 1023px) {
          .${className} {
            column-count: ${options.tablet.columnCount ?? 1};
            column-gap: ${options.tablet.gap ?? 24}px;
          }

          .${className} > * {
            break-inside: avoid;
            margin-bottom: ${options.tablet.gap ?? 24}px;
          }
        }

        @media only screen and (min-width: 1024px) {
          .${className} {
              column-count: ${options.desktop.columnCount ?? 1};
              column-gap: ${options.desktop.gap ?? 24}px;
            }

            .${className} > * {
              break-inside: avoid;
              margin-bottom: ${options.desktop.gap ?? 24}px;
            }
          }
        }
  `;

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: css }} />

      <div className={className}>
        {[...Array(props.options[viewport].itemCount)].map((_, i) => (
          <DropZone key={i} zone={`item-${i}`} />
        ))}
      </div>
    </>
  );
};

export const PuckMasonryGrid: ComponentConfig<MasonryGridProps> = {
  label: "Masonry Grid",
  fields: {
    options: {
      type: "object",
      label: "Options",
      objectFields: {
        desktop: {
          type: "object",
          label: "Desktop",
          objectFields: {
            itemCount: { type: "number", label: "Item Count", min: 1 },
            columnCount: { type: "number", label: "Number of Columns", min: 1 },
            gap: { type: "number", label: "Gap (px)", min: 0 },
            placeContent: {
              type: "select",
              label: "Place Content",
              options: [
                { label: "Normal", value: "normal" },
                { label: "Center", value: "center" },
                { label: "End", value: "end" },
                { label: "Start", value: "start" },
              ],
            },
          },
        },
        tablet: {
          type: "object",
          label: "Tablet",
          objectFields: {
            itemCount: { type: "number", label: "Item Count", min: 1 },
            columnCount: { type: "number", label: "Number of Columns", min: 1 },
            gap: { type: "number", label: "Gap (px)", min: 0 },
            placeContent: {
              type: "select",
              label: "Place Content",
              options: [
                { label: "Normal", value: "normal" },
                { label: "Center", value: "center" },
                { label: "End", value: "end" },
                { label: "Start", value: "start" },
              ],
            },
          },
        },
        mobile: {
          type: "object",
          label: "Mobile",
          objectFields: {
            itemCount: { type: "number", label: "Item Count", min: 1 },
            columnCount: { type: "number", label: "Number of Columns", min: 1 },
            gap: { type: "number", label: "Gap (px)", min: 0 },
            placeContent: {
              type: "select",
              label: "Place Content",
              options: [
                { label: "Normal", value: "normal" },
                { label: "Center", value: "center" },
                { label: "End", value: "end" },
                { label: "Start", value: "start" },
              ],
            },
          },
        },
      },
    },
  },
  defaultProps: {
    options: {
      desktop: {
        itemCount: 1,
        columnCount: 1,
        gap: 24,
        placeContent: "normal",
      },
      tablet: {
        itemCount: 1,
        columnCount: 1,
        gap: 24,
        placeContent: "normal",
      },
      mobile: {
        itemCount: 1,
        columnCount: 1,
        gap: 24,
        placeContent: "normal",
      },
    },
  },
  render: (props) => <MasonryGrid {...props} />,
};
